import { queueMacrotask } from 'module/helpers/macrotask';
import { Component, ComponentType, createRef, RefObject } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import type { SelectInstance } from 'react-select';

type KnownProps<T> = {
  selectRef: RefObject<SelectInstance>;
  onChange: (next: T) => void;
};

export const withFocusableSelect = <T, P extends KnownProps<T>>(
  OriginalComponent: ComponentType<React.PropsWithChildren<P>>,
) =>
  setDisplayName(wrapDisplayName(OriginalComponent, 'withFocusableSelect'))(
    // eslint-disable-next-line react/display-name
    class extends Component<Omit<P, 'selectRef'>> {
      selectRef = createRef<SelectInstance>();

      componentDidMount(): void {
        this.focus();
      }

      focus = () => {
        this.selectRef.current?.focus();
      };

      private _onChange = (next: T) => {
        const { onChange } = this.props;

        onChange(next);
        queueMacrotask(() => {
          this.selectRef.current?.blur();
          this.selectRef.current?.focus();
        });
      };

      render() {
        const { selectRef, _onChange, props } = this;

        return (
          <OriginalComponent
            selectRef={selectRef}
            {...(props as any)}
            onChange={_onChange}
          />
        );
      }
    },
  );
